import { useCallback, useMemo, useState } from "react";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { startAnalyzeImageLayers } from "../../../../actions/imageLayers";
import { getIsBlockedActions } from "../../../../actions/getters";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import { ALERTS_ASYNC_STATUS } from "../../../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../../../utils/errors";

const useAnalyzeImageLayers = ({
  sourceUrl,
  alertStatus,
  jobID,
  contentID,
  isCarousel = false,
  carouselIndex,
}) => {
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_ANALYZE_IMAGE_LAYERS_ACTION } = useCreditActionCosts();
  const [isStarting, setIsStarting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const matchingAlert = useSelectorApp((state) => {
    const alerts = state?.alerts?.notifications || [];
    return alerts.find((alert) => {
      if (alert?.jobType !== CREATIVE_JOB_TYPES.analyzeImageLayers) {
        return false;
      }
      if (jobID && (alert.jobID === jobID || alert.id === jobID)) {
        return true;
      }
      const alertUrl =
        alert?.generationData?.sourceUrl || alert?.generationData?.url;
      return Boolean(sourceUrl) && alertUrl === sourceUrl;
    });
  });

  const isAlertLoading = matchingAlert
    ? matchingAlert.status === ALERTS_ASYNC_STATUS.LOADING ||
      matchingAlert.status === ALERTS_ASYNC_STATUS.GENERATING
    : alertStatus === ALERTS_ASYNC_STATUS.LOADING ||
      alertStatus === ALERTS_ASYNC_STATUS.GENERATING;
  const isAlertError = matchingAlert
    ? matchingAlert.status === ALERTS_ASYNC_STATUS.ERROR
    : alertStatus === ALERTS_ASYNC_STATUS.ERROR;
  const isAnalyzing = isStarting || isAlertLoading;
  const errorMessage =
    localError || (isAlertError ? matchingAlert?.message : null);

  const analyze = useCallback(async () => {
    if (!sourceUrl || isAnalyzing) return { ok: false };

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_ANALYZE_IMAGE_LAYERS_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: 1,
      })
    );
    if (isBlocked) {
      return { ok: false };
    }

    setLocalError(null);
    setIsStarting(true);
    try {
      const response = await dispatch(
        startAnalyzeImageLayers({
          url: sourceUrl,
          contentID,
          isCarousel,
          carouselIndex,
        })
      );
      if (!response?.ok) {
        setLocalError(response?.message || null);
        return { ok: false };
      }
      return { ok: true };
    } finally {
      setIsStarting(false);
    }
  }, [
    ADS_CAMPAIGN_ANALYZE_IMAGE_LAYERS_ACTION,
    carouselIndex,
    contentID,
    dispatch,
    isAnalyzing,
    isCarousel,
    sourceUrl,
  ]);

  return useMemo(
    () => ({
      analyze,
      isAnalyzing,
      errorMessage,
      matchingAlert,
    }),
    [analyze, errorMessage, isAnalyzing, matchingAlert]
  );
};

export default useAnalyzeImageLayers;
